import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';

// --- Coverage level resolution ---

export type CoverageLevel = 'ok' | 'low' | 'critical';

export function resolveCoverageLevel(
  totalMembers: number,
  offCount: number
): CoverageLevel {
  if (totalMembers === 0) return 'ok';
  const availablePct = ((totalMembers - offCount) / totalMembers) * 100;
  if (availablePct < 40) return 'critical';
  if (availablePct < 70) return 'low';
  return 'ok';
}

// --- CVA variants ---

const indicatorVariants = cva('font-medium', {
  variants: {
    variant: {
      badge: 'inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[9px]',
      bar: 'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm',
    },
  },
  defaultVariants: {
    variant: 'badge',
  },
});

const levelVariants = cva('', {
  variants: {
    level: {
      ok: 'bg-green-100 text-black',
      low: 'bg-yellow-100 text-black',
      critical: 'bg-red-100 text-black',
    },
  },
  defaultVariants: {
    level: 'ok',
  },
});

const barProgressVariants = cva('h-2 rounded-full transition-all', {
  variants: {
    level: {
      ok: 'bg-green-500',
      low: 'bg-yellow-500',
      critical: 'bg-red-500',
    },
  },
  defaultVariants: {
    level: 'ok',
  },
});

// --- Props ---

export interface CoverageIndicatorProps
  extends VariantProps<typeof indicatorVariants> {
  totalMembers: number;
  offCount: number;
  className?: string;
}

// --- Component ---

export function CoverageIndicator({
  totalMembers,
  offCount,
  variant = 'badge',
  className,
}: CoverageIndicatorProps) {
  const { t } = useTranslation('leaves');
  const level = resolveCoverageLevel(totalMembers, offCount);
  const available = totalMembers - offCount;

  const hintMap: Record<CoverageLevel, string> = {
    ok: t('coverageOk'),
    low: t('coverageLow'),
    critical: t('coverageCritical'),
  };

  const labelMap: Record<CoverageLevel, string> = {
    ok: t('OK'),
    low: t('Low'),
    critical: t('Critical'),
  };

  const ariaLabel = t('coverageAriaLabel', {
    available,
    total: totalMembers,
    defaultValue: 'Team coverage: {{available}} of {{total}} available',
  });

  if (variant === 'bar') {
    const coveragePct = totalMembers > 0
      ? Math.round((available / totalMembers) * 100)
      : 100;

    return (
      <div
        className={cn(indicatorVariants({ variant: 'bar' }), levelVariants({ level }), className)}
        aria-label={ariaLabel}
        data-testid="coverage-indicator"
      >
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium">{labelMap[level]}</span>
            <span className="font-semibold">{coveragePct}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={barProgressVariants({ level })}
              style={{ width: `${coveragePct}%` }}
            />
          </div>
          <div className="mt-1 text-xs">
            {t('coverageAvailableCount', {
              available,
              total: totalMembers,
              defaultValue: '{{available}} of {{total}} available',
            })}
          </div>
        </div>
      </div>
    );
  }

  // Badge variant
  if (offCount === 0) return null;

  return (
    <div
      className={cn(indicatorVariants({ variant: 'badge' }), levelVariants({ level }), className)}
      title={hintMap[level]}
      aria-label={ariaLabel}
      data-testid="coverage-indicator"
    >
      {offCount}
    </div>
  );
}
